/**
 * Date utility functions for BST-safe date handling.
 *
 * IMPORTANT — TIMEZONE RULES:
 *   • NEVER use  date.toISOString().split('T')[0]  to get a YYYY-MM-DD string.
 *     toISOString() converts to UTC, shifting dates back 1 day in BST.
 *   • NEVER use  new Date('YYYY-MM-DD')  to parse a date-only string.
 *     It parses as UTC midnight, which is 23:00 the previous day in BST.
 *   • ALWAYS use  toLocalDateString(date)  to serialise a Date → 'YYYY-MM-DD'.
 *   • ALWAYS use  parseDateString(str)     to parse   'YYYY-MM-DD' → Date.
 */

/**
 * Factory function to create a new Date instance safely
 * @param {...any} args - Arguments to pass to Date constructor
 * @returns {Date} New Date instance
 */
export function createDate(...args) {
  return new Date(...args)
}

/**
 * Parse a YYYY-MM-DD date string as local midnight (not UTC).
 * new Date('2025-03-30') parses as UTC midnight, which shifts to the
 * previous day in BST/positive-offset timezones. This helper avoids that.
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date} Date object at local midnight
 */
export function parseDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') return new Date(NaN)
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Convert a Date object to YYYY-MM-DD using LOCAL time (not UTC).
 * This is the safe replacement for  date.toISOString().split('T')[0]
 * which shifts dates back 1 day in BST/positive-offset timezones.
 * @param {Date} date - Date object to convert
 * @returns {string} Date in YYYY-MM-DD format (local time)
 */
export function toLocalDateString(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Get current year safely
 * @returns {number} Current year
 */
export function getCurrentYear() {
  const now = createDate()
  return now.getFullYear()
}

/**
 * Get today's date as YYYY-MM-DD string using LOCAL time.
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayISOString() {
  return toLocalDateString(createDate())
}

/**
 * Convert a Date or string to YYYY-MM-DD format using LOCAL time.
 * @param {Date|string} date - Date to convert (Date object or ISO string)
 * @returns {string} Date in YYYY-MM-DD format
 */
export function dateToISOString(date) {
  if (typeof date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date
    }
    // Full ISO string or other format — parse then extract local date
    date = new Date(date)
  }
  return toLocalDateString(date)
}

/**
 * Get year start date (January 1st of given year)
 * @param {number} year - Year to get start date for
 * @returns {Date} Start of year date
 */
export function getYearStart(year) {
  return createDate(year, 0, 1)
}

/**
 * Get year start date from another date
 * @param {Date} date - Date to get year start from
 * @returns {Date} Start of year date
 */
export function getYearStartFromDate(date) {
  const year = date.getFullYear()
  return createDate(year, 0, 1)
}

/**
 * Get date from N days ago
 * @param {number} days - Number of days ago
 * @returns {Date} Date N days ago
 */
export function getDaysAgo(days) {
  const now = createDate()
  const timestamp = now.getTime()
  const daysInMs = days * 24 * 60 * 60 * 1000
  return createDate(timestamp - daysInMs)
}

/**
 * Get date from N months ago
 * @param {number} months - Number of months ago
 * @returns {Date} Date N months ago
 */
export function getMonthsAgo(months) {
  const now = createDate()
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = now.getDate()
  return createDate(year, month - months, day)
}

/**
 * Get full ISO timestamp with time (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @returns {string} Full ISO timestamp
 */
export function getISOTimestamp() {
  const now = createDate()
  return now.toISOString()
}

/**
 * Get number of days in a month
 * @param {number} year - Year
 * @param {number} month - Month (0-indexed, 0 = January)
 * @returns {number} Number of days in the month
 */
export function getDaysInMonth(year, month) {
  // Create date for the first day of next month, then get date 0 (last day of current month)
  const lastDay = createDate(year, month + 1, 0)
  return lastDay.getDate()
}

/**
 * Format a date string using locale formatting
 * @param {string|Date} dateInput - Date string or Date object to format
 * @param {string} locale - Locale string (default: 'en-GB')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export function formatDateLocale(dateInput, locale = 'en-GB', options = {}) {
  if (!dateInput) return ''
  const date = typeof dateInput === 'string' ? parseDateString(dateInput) : dateInput
  return date.toLocaleDateString(locale, options)
}

/**
 * Format a date with relative day names for nearby dates
 * - Today → "Today"
 * - Tomorrow → "Tomorrow"
 * - Yesterday → "Yesterday"
 * - Within next 6 days → Day name (e.g., "Sunday")
 * - All other dates → Standard format (e.g., "1 Dec 25")
 *
 * @param {string|Date} dateInput - Date string (YYYY-MM-DD) or Date object
 * @returns {string} Formatted relative date string
 */
export function formatRelativeDate(dateInput) {
  if (!dateInput) return ''

  // Parse the input date (handle YYYY-MM-DD string format)
  let targetDate
  if (typeof dateInput === 'string') {
    // Parse YYYY-MM-DD without timezone issues
    const [year, month, day] = dateInput.split('-').map(Number)
    targetDate = createDate(year, month - 1, day)
  } else {
    targetDate = dateInput
  }

  // Get today at midnight for comparison
  const today = createDate()
  today.setHours(0, 0, 0, 0)
  targetDate.setHours(0, 0, 0, 0)

  // Calculate difference in days
  const diffTime = targetDate.getTime() - today.getTime()
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

  // Today
  if (diffDays === 0) {
    return 'Today'
  }

  // Tomorrow
  if (diffDays === 1) {
    return 'Tomorrow'
  }

  // Yesterday
  if (diffDays === -1) {
    return 'Yesterday'
  }

  // Future dates within 7 days - show day name
  if (diffDays > 1 && diffDays <= 6) {
    return targetDate.toLocaleDateString('en-GB', { weekday: 'long' })
  }

  // All other dates - show standard format (e.g., "1 Dec 25")
  return targetDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  })
}